import {
  DEFAULT_STAT_TYPE_DATA,
  STAT_TYPES_TABLE_HEADER
} from '@bo-constants/statTypes';
import type {
  SortStatTypeColumn,
  StatTypesQueryParams
} from '@library/types/statType';
import { TableBody, TableHeader } from '@library/components/table';
import { useCallback, useEffect, useState } from 'react';
import { Checkbox } from '@library/components/inputs';
import { DEFAULT_STAT_TYPE_SORT } from '@library/constants/sorting';
import { DateTime } from 'luxon';
import { GlobalFilters } from '@library/components/GlobalFilters';
import { Loading } from '@library/components/Loading';
import { PaginationButtons } from '@library/components/buttons';
import RowsPerPage from '@library/components/table/RowsPerPage';
import { StatTypeDetailsModal } from '@bo-components/statTypes/StatTypeDetailsModal';
import { StyledTable } from '@bo-components/common/StyledTable';
import { StyledTableRow } from '@bo-components/common/StyledTableRow';
import UtilDashboardHeader from '@bo-components/common/UtilDashboardHeader';
import { selectHasEditStatTypes } from '@bo-selectors/utilsDashes';
import { useDashBoard } from '@bo-hooks/useDashboard';
import { usePerPage } from '@bo-hooks/usePerPage';
import { useSelector } from 'react-redux';
import { useSentrySetDashBoardTag } from '@bo-hooks/sentry/useSentrySetDashBoardTag';
import { useStatTypes } from '@bo-hooks/utilsDashboards/useStatTypes';
import useUtilDashboard from '@bo-hooks/utilsDashboards/useUtilDashboard';
import { withProfiler } from '@sentry/react';

export const StatTypeDashboardView = () => {
  useSentrySetDashBoardTag({ dashboardName: 'StatType' });

  const {
    durations,
    fromDate,
    handleSearch,
    handleSelectDateRange,
    handleSelectLeague,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    searchValue,
    selectedEndDate,
    selectedStartDate,
    toDate
  } = useDashBoard({
    initialStartDate: DateTime.fromISO('2018-01-01'),
    dashboardName: 'STAT_TYPES'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<SortStatTypeColumn>(DEFAULT_STAT_TYPE_SORT);
  const [submitMessage, setSubmitMessage] = useState('');
  const [currentItemId, setCurrentItemId] = useState('');

  const { handleSelectPerPage, selectedPerPage } = usePerPage();

  const { id: leagueId } = leagueInfo ?? {};
  const statTypesQueryParams = {
    fromDate,
    leagueId,
    page: currentPage,
    perPage: selectedPerPage,
    searchValue,
    sortBy: sort.sortBy,
    sortDir: sort.sortDirection,
    toDate
  } as StatTypesQueryParams;

  const {
    isError,
    isLoading,
    statTypes,
    totalPages,
    createStatType,
    updateStatType
  } = useStatTypes({
    statTypesQueryParams,
    leaguesList,
    currentItemId,
    setSubmitMessage
  });

  const {
    allItemsChecked,
    currentItemData,
    isDetailsModalOpen,
    idsForCSV,
    isGeneratingCSV,
    isNewItem,
    handleClickAllItemsCheckbox,
    handleClickCheckbox,
    handleCSVExportClick,
    handleAddNewItemClick,
    handleCloseModal,
    handleClickRow
  } = useUtilDashboard({
    data: statTypes,
    path: 'stat_types',
    defaultItemData: DEFAULT_STAT_TYPE_DATA,
    setSubmitMessage
  });

  useEffect(() => {
    if (currentItemData.id) {
      setCurrentItemId(currentItemData.id);
    }
  }, [currentItemData.id]);

  const handleSort = useCallback(
    (sortColumn: SortStatTypeColumn) => {
      setSort(sortColumn);
    },
    [setSort]
  );

  const hasCreateOrEditStatTypes = useSelector(selectHasEditStatTypes);

  if (isLeagueLoading || isLoading) {
    return <Loading loadingMessage="Loading Leagues" />;
  }

  return (
    <div className="h-full w-auto">
      <UtilDashboardHeader
        isCSVExportDisabled={idsForCSV.length === 0}
        isGeneratingCSV={isGeneratingCSV}
        handleCSVExportClick={handleCSVExportClick}
        handleCreateNewItemClick={handleAddNewItemClick}
        pageTitle="Stat Types"
        isCreateAllowed={hasCreateOrEditStatTypes}
      />
      <div className="flex flex-col gap-4 p-6">
        <GlobalFilters
          allowPastDates
          className="grid-cols-[fit-content(100px)_minmax(18rem,25rem)_minmax(11rem,1fr)]"
          dateFormat="MMM d, yyyy"
          disabled={false}
          durations={durations}
          handleSearch={handleSearch}
          handleSelectDateRange={handleSelectDateRange}
          handleSelectLeague={handleSelectLeague}
          searchDisabled={false}
          searchValue={searchValue}
          leaguesList={leaguesList}
          selectedEndDate={selectedEndDate}
          selectedStartDate={selectedStartDate}
          selectedLeagueId={leagueId}
        />
        <StyledTable
          errorMessage="Error Fetching Stat Types"
          isError={isError}
          noData={!statTypes?.length}
          noDataMessage={{
            label: "Couldn't find any Stat Types",
            title: 'No Stat Types',
            dataTestId: 'no-stat-types-results'
          }}
        >
          <TableHeader
            headers={STAT_TYPES_TABLE_HEADER}
            sort={sort}
            onSort={handleSort}
          >
            <Checkbox
              id="stat-type-all-checkbox"
              checked={allItemsChecked}
              onCheckboxChange={handleClickAllItemsCheckbox}
            />
          </TableHeader>
          <TableBody>
            {statTypes?.map((statType) => (
              <StyledTableRow
                headers={STAT_TYPES_TABLE_HEADER}
                key={`statType-${statType.id}`}
                rowData={statType}
                onClick={handleClickRow}
                onClickCheckbox={handleClickCheckbox}
                isAllChecked={allItemsChecked}
              />
            ))}
          </TableBody>
        </StyledTable>
        <div className="bg-soFresh-100 border-soFresh-140 sticky bottom-0 flex items-center justify-between gap-4 border-t">
          <RowsPerPage
            handleSelectPerPage={handleSelectPerPage}
            offset={173}
            placement="top-start"
            selectedPerPage={selectedPerPage}
          />
          <PaginationButtons
            currentPage={currentPage}
            handlePageChange={setCurrentPage}
            totalPages={totalPages}
          />
        </div>
      </div>
      <StatTypeDetailsModal
        isNewItem={isNewItem}
        isOpen={isDetailsModalOpen}
        onClose={handleCloseModal}
        onSubmit={isNewItem ? createStatType : updateStatType}
        modalData={currentItemData}
        submitMessage={submitMessage}
      />
    </div>
  );
};

export const StatTypeDashboard = withProfiler(StatTypeDashboardView);
